import { Link } from 'react-router-dom';
import { 
  ArrowUpRight, Building2, Handshake, Heart, 
  GraduationCap, Briefcase, Gift, CheckCircle
} from 'lucide-react';

const Partners = () => {
  const partnerTypes = [
    {
      icon: Building2,
      title: 'Corporate Partners',
      description: 'Partner with us to build your future workforce pipeline while making a meaningful impact.',
      benefits: [
        'Access to trained, job-ready talent',
        'Early engagement with emerging tech talent',
        'Brand visibility in the community',
        'Tax benefits for donations'
      ]
    },
    {
      icon: GraduationCap,
      title: 'Educational Partners',
      description: 'Schools and universities can extend their tech offerings through our programs.',
      benefits: [
        'Supplement existing curriculum',
        'Provide students hands-on experience',
        'Bridge classroom to career',
        'Joint program development'
      ]
    },
    {
      icon: Handshake,
      title: 'Community Partners',
      description: 'Nonprofits and community organizations can help us reach more youth.',
      benefits: [
        'Expand program reach',
        'Co-host events and workshops',
        'Share resources and networks',
        'Collaborative grant opportunities'
      ]
    }
  ];

  const waysToPartner = [
    {
      icon: Briefcase,
      title: 'Mentorship',
      description: 'Connect your team with students for guidance and industry insights.'
    },
    {
      icon: Gift,
      title: 'Sponsorship',
      description: 'Support cohorts financially and get recognition for your investment.'
    },
    {
      icon: Heart,
      title: 'In-Kind Donations',
      description: 'Provide equipment, software licenses, or workspace for students.'
    }
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="py-24 sm:py-32 bg-[#060F22] relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-[#0A1F44] via-[#060F22] to-[#060F22]"></div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <span className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#00FFE0] mb-6 block">
              Partnership Opportunities
            </span>
            <h1 className="text-5xl sm:text-6xl font-black tracking-tighter text-white leading-tight mb-6">
              Build the Future{' '}
              <span className="gradient-text">Together</span>
            </h1>
            <p className="text-lg sm:text-xl text-[#94A3B8] leading-relaxed mb-8">
              Partner with NextGen Tech Pathways to create real career opportunities for youth while building your future talent pipeline.
            </p>
            <Link
              to="/contact"
              data-testid="partners-hero-contact-btn"
              className="inline-flex items-center justify-center bg-[#FF6A00] text-white font-bold rounded-full px-8 py-4 shadow-[0_0_20px_rgba(255,106,0,0.3)] hover:shadow-[0_0_30px_rgba(255,106,0,0.5)] hover:-translate-y-1 transition-all duration-300 gap-2 text-lg"
            >
              Become a Partner
              <ArrowUpRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Partner Section */}
      <section className="py-24 sm:py-32 bg-[#0A1F44]/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#00FFE0] mb-4 block">
              Why Partner With Us
            </span>
            <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-white mb-6">
              Make an Impact That Matters
            </h2>
            <p className="text-lg text-[#94A3B8] leading-relaxed">
              When you partner with NextGen Tech Pathways, you're not just sponsoring a program—you're investing in the future of technology and your community.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {partnerTypes.map((type, index) => (
              <div
                key={index}
                className="bg-[#0A1F44]/50 backdrop-blur-md border border-[#00AEEF]/20 rounded-2xl p-8 hover:border-[#00FFE0]/50 transition-all duration-500"
              >
                <div className="w-16 h-16 rounded-2xl bg-[#00AEEF]/20 flex items-center justify-center mb-6">
                  <type.icon className="w-8 h-8 text-[#00AEEF]" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">{type.title}</h3>
                <p className="text-[#94A3B8] leading-relaxed mb-6">{type.description}</p>
                <ul className="space-y-3">
                  {type.benefits.map((benefit, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[#00FFE0] flex-shrink-0 mt-0.5" />
                      <span className="text-[#94A3B8] text-sm">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ways to Partner */}
      <section className="py-24 sm:py-32 bg-[#060F22]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#00FFE0] mb-4 block">
              Ways to Partner
            </span>
            <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-white mb-6">
              Choose Your Impact
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {waysToPartner.map((way, index) => (
              <div
                key={index}
                className="bg-[#0A1F44]/50 backdrop-blur-md border border-[#00AEEF]/20 rounded-2xl p-8 text-center hover:border-[#00FFE0]/50 transition-all duration-500"
              >
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#00AEEF] to-[#00FFE0] mx-auto mb-6 flex items-center justify-center">
                  <way.icon className="w-8 h-8 text-[#060F22]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{way.title}</h3>
                <p className="text-[#94A3B8] leading-relaxed">{way.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Current Partners Placeholder */}
      <section className="py-24 sm:py-32 bg-[#0A1F44]/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#00FFE0] mb-4 block">
              Our Partners
            </span>
            <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-white mb-6">
              Join Our Growing Network
            </h2>
            <p className="text-lg text-[#94A3B8] leading-relaxed">
              We're actively seeking partners who share our vision of creating tech opportunities for all youth.
            </p>
          </div>

          <div className="bg-[#0A1F44]/50 backdrop-blur-md border border-[#00AEEF]/20 rounded-2xl p-12 text-center">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 items-center opacity-50">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-16 bg-[#060F22] rounded-xl flex items-center justify-center">
                  <span className="text-[#94A3B8] text-sm">Your Logo Here</span>
                </div>
              ))}
            </div>
            <p className="text-[#94A3B8] mt-8">
              Be among the first organizations to partner with NextGen Tech Pathways and make a lasting impact.
            </p>
          </div>
        </div>
      </section>

      {/* Impact Stats */}
      <section className="py-24 sm:py-32 bg-[#060F22]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="bg-gradient-to-r from-[#0A1F44] to-[#0A1F44]/50 rounded-3xl p-12 border border-[#00AEEF]/20">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <span className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#00FFE0] mb-4 block">
                  Partner Impact
                </span>
                <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white mb-6">
                  Your Partnership Dollars at Work
                </h2>
                <p className="text-[#94A3B8] leading-relaxed">
                  Every dollar invested in NextGen Tech Pathways goes directly toward creating opportunities for youth. Here's what your partnership can fund:
                </p>
              </div>
              <div className="grid grid-cols-2 gap-6">
                <div className="bg-[#060F22]/50 rounded-xl p-6 text-center">
                  <div className="text-3xl font-black text-[#FF6A00] mb-2">$500</div>
                  <p className="text-[#94A3B8] text-sm">Sponsors one student for a full cohort</p>
                </div>
                <div className="bg-[#060F22]/50 rounded-xl p-6 text-center">
                  <div className="text-3xl font-black text-[#00AEEF] mb-2">$2,500</div>
                  <p className="text-[#94A3B8] text-sm">Funds equipment for 5 students</p>
                </div>
                <div className="bg-[#060F22]/50 rounded-xl p-6 text-center">
                  <div className="text-3xl font-black text-[#00FFE0] mb-2">$5,000</div>
                  <p className="text-[#94A3B8] text-sm">Sponsors an entire Demo Day event</p>
                </div>
                <div className="bg-[#060F22]/50 rounded-xl p-6 text-center">
                  <div className="text-3xl font-black text-white mb-2">$10,000</div>
                  <p className="text-[#94A3B8] text-sm">Becomes a Cohort Sponsor</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 sm:py-32 bg-[#0A1F44]/30 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-[#00AEEF]/10 via-transparent to-transparent"></div>
        <div className="relative max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-white mb-6">
            Ready to Make an Impact?
          </h2>
          <p className="text-lg text-[#94A3B8] leading-relaxed mb-8 max-w-2xl mx-auto">
            Let's discuss how your organization can partner with NextGen Tech Pathways to build the future of tech talent together.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              data-testid="partners-cta-contact-btn"
              className="inline-flex items-center justify-center bg-[#FF6A00] text-white font-bold rounded-full px-8 py-4 shadow-[0_0_20px_rgba(255,106,0,0.3)] hover:shadow-[0_0_30px_rgba(255,106,0,0.5)] hover:-translate-y-1 transition-all duration-300 gap-2"
            >
              Contact Us
              <ArrowUpRight className="w-5 h-5" />
            </Link>
            <a
              href="mailto:partnerships@nextgentechpath.org"
              data-testid="partners-email-btn"
              className="inline-flex items-center justify-center bg-transparent border-2 border-[#00AEEF] text-[#00AEEF] hover:bg-[#00AEEF]/10 hover:text-white font-bold rounded-full px-8 py-4 transition-all duration-300 gap-2"
            >
              partnerships@nextgentechpath.org
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Partners;
